import React from "react"

import { CalendarIcon } from "lucide-react"
import { DateRange } from "react-day-picker"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { formatDayMonthYear } from "@/lib/dateFormats"
import { cn } from "@/lib/utils"
import { type ViewMode, WEEKDAY_NAMES } from "@/lib/weekdayComparison"

import { type DatePreset } from "./useDateRangePreset"

interface LoadFactorFiltersProps {
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  /** 0 = Sunday. Only offered in weekday mode. */
  selectedWeekday: number
  onSelectedWeekdayChange: (weekday: number) => void
  datePreset: DatePreset
  onDatePresetChange: (preset: DatePreset) => void
  dateRange: DateRange | undefined
  onDateRangeChange: (range: DateRange | undefined) => void
}

const LoadFactorFilters: React.FC<LoadFactorFiltersProps> = ({
  viewMode,
  onViewModeChange,
  selectedWeekday,
  onSelectedWeekdayChange,
  datePreset,
  onDatePresetChange,
  dateRange,
  onDateRangeChange,
}) => (
  <Card>
    <CardHeader>
      <CardTitle>Filters</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-sm font-medium text-palette-jungleGreen700 mb-2 block">
            View Mode
          </label>
          <Select value={viewMode} onValueChange={(v) => onViewModeChange(v as ViewMode)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weekday">Week View</SelectItem>
              <SelectItem value="week">Day View</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {viewMode === "weekday" && (
          <div className="flex-1 min-w-[200px]">
            <label className="text-sm font-medium text-palette-jungleGreen700 mb-2 block">
              Weekday
            </label>
            <Select
              value={selectedWeekday.toString()}
              onValueChange={(v) => onSelectedWeekdayChange(parseInt(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WEEKDAY_NAMES.map((name, index) => (
                  <SelectItem key={index} value={index.toString()}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex-1 min-w-[200px]">
          <label className="text-sm font-medium text-palette-jungleGreen700 mb-2 block">
            Date Range
          </label>
          <Select value={datePreset} onValueChange={onDatePresetChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1week">Last 1 week</SelectItem>
              <SelectItem value="2weeks">Last 2 weeks</SelectItem>
              <SelectItem value="4weeks">Last 4 weeks</SelectItem>
              <SelectItem value="8weeks">Last 8 weeks</SelectItem>
              <SelectItem value="12weeks">Last 12 weeks</SelectItem>
              <SelectItem value="custom">Custom range</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {datePreset === "custom" && (
          <div className="flex-1 min-w-[300px]">
            <label className="text-sm font-medium text-palette-jungleGreen700 mb-2 block">
              Select Dates
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !dateRange && "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange?.from ? (
                    dateRange.to ? (
                      <>
                        {formatDayMonthYear(dateRange.from)} -{" "}
                        {formatDayMonthYear(dateRange.to)}
                      </>
                    ) : (
                      formatDayMonthYear(dateRange.from)
                    )
                  ) : (
                    <span>Pick a date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent variant="plain" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange?.from}
                  selected={dateRange}
                  onSelect={(range) => {
                    onDateRangeChange(range)
                  }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
)

export default LoadFactorFilters
